import {
  Text,
  Box,
  Heading,
  Image,
  Flex,
  Button,
  Link,
  theme,
} from '@everlywell/ui-kit';
import { useLocation } from '@gatsbyjs/reach-router';
import DynamicIcon, { IconKey } from 'components/DynamicIcon';
import { useJustunoLoader } from 'components/JustunoLoader/useJustunoLoader';
import BulletPointWithIcon from 'components/PageSections/components/BulletPointWithIcon';
import ProductPricingButton from 'components/ProductPricingButton';
import VideoComponent, { VideoComponentProps } from 'components/VideoComponent';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import React, { useEffect, useState } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { sanitize } from 'utils/domHelpers';
import { isBrowser } from 'utils/helpers';
import { replaceRedirectionLinkRoot } from 'utils/helpers/replaceRedirectionLinkRoot';
import useMdpModals from 'utils/hooks/useMdpModals';
import { ImageType } from 'utils/types';

import SectionContainer from '../components/SectionContainer';
import { findContentfulFlagValue } from '../utils';

export type HeroV3BulletPoint = {
  iconName?: string | null;
  iconColor?: string | null;
  text?: string | null;
  textColor?: string | null;
};

export type HeroV3Props = {
  backgroundColor?: string | null;
  bulletPoints?: HeroV3BulletPoint[] | null;
  cta?: Queries.Maybe<Queries.ContentfulCallToAction001>;
  desktopImage?: ImageType | null;
  eyebrow?: string | null;
  eyebrowColor?: string | null;
  eyebrowIcon?: Queries.Maybe<Queries.ContentfulAsset> | null;
  hideClipPath?: boolean;
  mobileImage?: ImageType | null;
  title?: string | null;
  titleColor?: string | null;
  titleTag?: string | null;
  disclaimer?: string | null;
  disclaimerColor?: string | null;
  mobileVideo?: VideoComponentProps | null;
  desktopVideo?: VideoComponentProps | null;
};

type ButtonClickHandler = (
  event: React.MouseEvent<HTMLButtonElement>,
  options?: { skipScreenerModal?: boolean },
) => void;

const DESKTOP_MAX_HEIGHT = '640px';
const DESKTOP_WITH_VIDEO_MAX_HEIGHT = '660px';
const MOBILE_WITH_VIDEO_CONTAINER_HEIGHT = '600px';
const TABLET_WITH_VIDEO_CONTAINER_HEIGHT = '700px';
const MOBILE_VIDEO_CONTAINER_HEIGHT = '360px';
const TABLET_VIDEO_CONTAINER_HEIGHT = '460px';

/**
 * HeroV3 component
 * Similar to HeroV2 but with a different layout. It has a title, eyebrow, bullet points, and a CTA button.
 * It also has a background image that changes based on the screen size.
 * @param {string} [props.eyebrow] Eyebrow text
 * @param {string} [props.eyebrowColor] Eyebrow text color
 * @param {Queries.Maybe<Queries.ContentfulAsset>} [props.eyebrowIcon] Eyebrow icon
 * @param {string} [props.title] Title text
 * @param {string} [props.titleColor] Title text color
 * @param {string} [props.backgroundColor] Background color
 * @param {string} [props.titleTag] Title tag
 * @param {HeroV3BulletPoint[]} [props.bulletPoints] Bullet points with icon and text
 * @param {HeroV3BulletPoint[]} [props.bulletPoints.iconName] Bullet point icon name
 * @param {HeroV3BulletPoint[]} [props.bulletPoints.iconColor] Bullet point icon color
 * @param {HeroV3BulletPoint[]} [props.bulletPoints.text] Bullet point text
 * @param {HeroV3BulletPoint[]} [props.bulletPoints.textColor] Bullet point text color
 * @param {Queries.Maybe<Queries.ContentfulCallToAction001>} props.cta Call to action button
 * @param {ImageType} props.desktopImage Desktop image
 * @param {ImageType} props.mobileImage Mobile image
 * @param {boolean} [props.hideClipPath] Whether to hide the clip path
 * @param {string} [props.disclaimer] Disclaimer text
 * @param {string} [props.disclaimerColor] Disclaimer text color
 * @param {VideoComponentProps} [props.mobileVideo] Video to display on mobile screens (centered in container)
 * @param {VideoComponentProps} [props.desktopVideo] Video to display on desktop screens
 * @returns {JSX.Element} HeroV3 component
 */
export default function HeroV3({
  backgroundColor,
  bulletPoints,
  cta,
  desktopImage,
  eyebrow,
  eyebrowColor,
  eyebrowIcon,
  hideClipPath,
  mobileImage,
  title,
  titleColor,
  titleTag,
  disclaimer,
  disclaimerColor,
  mobileVideo,
  desktopVideo,
}: HeroV3Props): JSX.Element {
  const { handleMdpCtaClick } = useMdpModals();
  const hideJustunoCta = useFlags().hideJustunoCTA;
  const location = useLocation();

  const {
    priceText,
    billFrequencyText,
    icon: ctaIcon,
    startingText,
    hideSeparator,
  } = cta?.metadata?.context?.ctaParts || {};

  const ctaFlags = cta?.metadata?.flags;
  const isStandardButton = findContentfulFlagValue(
    'isStandardButton',
    ctaFlags,
  );
  const isJustunoLoadingState = findContentfulFlagValue(
    'isJustunoLoadingState',
    ctaFlags,
  );
  const { isLoading, setJustUnoLoader } = useJustunoLoader({
    isJustunoLoadingState,
  });

  const skipScreenerModal =
    findContentfulFlagValue('skipScreenerModal', ctaFlags) ?? false;

  const shouldHideCTA = () => {
    /*
      This is a temporary solution

      Issue: Justuno script is loaded through GTM, on Safari private mode, the script is not loaded.
      Solution: Hide Justuno CTA when Justuno script is not loaded.
    */
    // @ts-ignore
    if (isBrowser() && isJustunoLoadingState && !window?._jst) {
      return true;
    }

    const includedPaths = hideJustunoCta?.includePaths || [];

    const hasMatch = includedPaths.some((path: string) => {
      const pathname = location.pathname.replace(/^\/|\/$/g, '').toLowerCase();
      const currentPath = path.replace(/^\/|\/$/g, '').toLowerCase();

      return pathname === currentPath;
    });

    return hideJustunoCta?.enabled && hasMatch;
  };

  const ctaUrl = cta?.url?.includes('http')
    ? replaceRedirectionLinkRoot(cta.url)
    : `${cta?.url}`;

  const hasUrl = cta?.url;
  const isInternalLink = cta?.url?.startsWith('/');

  const handleCTAClick: ButtonClickHandler = (event, options) => {
    trackCtaClick();

    const { skipScreenerModal } = options || {};

    setJustUnoLoader();

    handleMdpCtaClick({
      event,
      ctaUrl: ctaUrl,
      skipScreenerModal,
    });
  };

  const trackCtaClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'HeroV3',
        ctaText: cta?.label || 'No CTA label',
      },
    });
  };

  const hasDesktopVideo = Boolean(desktopVideo);
  const hasMobileVideo = Boolean(mobileVideo);

  // State to track if page is loaded to play video
  const [shouldPlayVideo, setShouldPlayVideo] = useState(false);

  useEffect(() => {
    setShouldPlayVideo(true);
  }, []);

  return (
    <SectionContainer
      size={['fluid', null, null, null, 'ultra']}
      padding="0"
      backgroundColor="white"
    >
      <Box
        width={[
          // On mobile, if there's a mobile video, add 16px margin on sides
          hasMobileVideo ? 'calc(100% - 16px)' : '100%',
          null,
          // On desktop, if there's a desktop video, add 48px margin on sides
          hasDesktopVideo ? 'calc(100% - 46px)' : '100%',
        ]}
        maxWidth={[null, null, hasDesktopVideo ? '1600px' : null]}
        marginX="auto"
        marginTop={[
          hasMobileVideo ? 4 : null,
          null,
          hasDesktopVideo ? 4 : null,
        ]}
        clipPath={
          hideClipPath
            ? 'none'
            : [
                'ellipse(250% 60% at 50% 40%)',
                null,
                'ellipse(120% 60% at 50% 40%)',
              ]
        }
        backgroundColor={backgroundColor || undefined}
        display="flex"
        alignItems="center"
        flexDirection={['column', null, 'row']}
        gap="12"
        position="relative"
        overflow="hidden"
        height={[
          hasMobileVideo ? MOBILE_WITH_VIDEO_CONTAINER_HEIGHT : null,
          hasMobileVideo ? TABLET_WITH_VIDEO_CONTAINER_HEIGHT : null,
          hasDesktopVideo ? DESKTOP_WITH_VIDEO_MAX_HEIGHT : null,
        ]}
        borderRadius={[
          hasMobileVideo ? '3xl' : null,
          null,
          hasDesktopVideo && hideClipPath ? '3xl' : null,
        ]}
      >
        {/* Video backgrounds, should look like a background of the container */}
        {mobileVideo ? (
          <Box
            display={['flex', null, 'none']}
            width="100%"
            height={[
              MOBILE_VIDEO_CONTAINER_HEIGHT,
              TABLET_VIDEO_CONTAINER_HEIGHT,
            ]}
            position="absolute"
            bottom={[-6, -20, null]}
            zIndex="0"
            justifyContent="center"
            alignItems="center"
          >
            <VideoComponent
              {...mobileVideo}
              objectFit="contain"
              height="auto"
              autoPlay={shouldPlayVideo}
            />
          </Box>
        ) : null}

        {desktopVideo ? (
          <Box
            display={['none', null, 'flex']}
            position="absolute"
            justifyContent="flex-end"
            bottom="0"
            right="0"
            overflow="hidden"
            zIndex="0"
          >
            <VideoComponent
              {...desktopVideo}
              objectFit="contain"
              height="auto"
              autoPlay={shouldPlayVideo}
            />
          </Box>
        ) : null}
        <Box
          paddingLeft={[6, null, 20]}
          paddingRight={[6, null, 'unset']}
          paddingTop={[12, null, hasDesktopVideo ? 20 : 6]}
          paddingBottom={[null, null, 12]}
          flex="1"
          position="relative"
          zIndex="2"
          display={[
            // Mobile
            'block',
            null,
            // Desktop
            hasDesktopVideo ? 'flex' : 'block',
          ]}
          flexDirection={[
            // Mobile
            'row',
            null,
            // Desktop
            hasDesktopVideo ? 'column' : 'row',
          ]}
          justifyContent={[
            // Mobile
            'center',
            null,
            // Desktop
            hasDesktopVideo ? 'space-between' : 'center',
          ]}
          height={hasDesktopVideo ? '100%' : 'auto'}
        >
          <Box>
            {eyebrow ? (
              <Flex alignItems="center" marginBottom="4">
                {eyebrowIcon ? (
                  <Image
                    // render as GatsbyImage if gatsbyImageData is available
                    {...(eyebrowIcon?.gatsbyImageData && {
                      as: GatsbyImage,
                      image: eyebrowIcon.gatsbyImageData,
                    })}
                    // render as regular Image if gatsbyImageData is not available
                    {...(!eyebrowIcon?.gatsbyImageData &&
                      eyebrowIcon?.file?.url && { src: eyebrowIcon.file.url })}
                    alt={eyebrowIcon.description || ''}
                    width="auto"
                    maxHeight={theme.space['6']}
                    marginRight="2"
                  />
                ) : null}
                <Text
                  color={eyebrowColor || 'white'}
                  fontSize={['lg', null, 'xl']}
                  width={['100%', null, 'fit-content']}
                  textAlign={['center', null, 'left']}
                >
                  {eyebrow}
                </Text>
              </Flex>
            ) : null}

            {title ? (
              <Heading
                as={(titleTag as React.ElementType) || 'h2'}
                color={titleColor || undefined}
                size="2xl"
                marginBottom={[8, null, 5]}
                width={['100%', null, 'fit-content']}
                textAlign={['center', null, 'left']}
              >
                {title}
              </Heading>
            ) : null}

            {bulletPoints && bulletPoints.length > 0 ? (
              <Flex display="flex" flexDirection="column" gap="2" role="list">
                {bulletPoints?.map((bulletPoint, index) => (
                  <BulletPointWithIcon
                    key={`bullet-${index}`}
                    text={bulletPoint.text || ''}
                    textColor={bulletPoint.textColor}
                    iconName={bulletPoint.iconName || ''}
                    iconColor={bulletPoint.iconColor}
                    weight="fill"
                  />
                ))}
              </Flex>
            ) : null}
          </Box>

          <Box>
            {cta && cta.label && !shouldHideCTA() ? (
              <Box
                marginTop={[
                  hasMobileVideo ? null : 8,
                  null,
                  hasDesktopVideo ? null : 8,
                ]}
                width={['100%', null, 'fit-content']}
              >
                {isStandardButton ? (
                  <Button
                    colorScheme="whiteCS"
                    onClick={(e) =>
                      handleCTAClick(e, { skipScreenerModal: true })
                    }
                    className={cta?.metadata?.metadata_id ?? ''}
                    isLoading={isLoading}
                    {...(hasUrl &&
                      !isInternalLink && { as: 'a', href: ctaUrl })}
                    {...(hasUrl && isInternalLink && { as: Link, to: ctaUrl })}
                  >
                    {cta.label}
                  </Button>
                ) : (
                  <ProductPricingButton
                    startingText={startingText || ''}
                    priceText={priceText || ''}
                    size="md"
                    billFrequencyText={billFrequencyText || ''}
                    hideSeparator={hideSeparator || false}
                    buttonText={cta.label}
                    colorScheme="greenCS"
                    buttonIconRight={
                      <DynamicIcon icon={(ctaIcon as IconKey) || 'ArrowDown'} />
                    }
                    onButtonClick={(e) =>
                      handleCTAClick(e, { skipScreenerModal })
                    }
                    linkTo={ctaUrl}
                    id="hero-v3-cta-button"
                    buttonProps={{
                      ...(cta?.metadata?.metadata_id && {
                        className: cta.metadata.metadata_id,
                      }),
                      ...(cta?.url === null && {
                        as: 'button',
                        type: 'button',
                      }),
                    }}
                  />
                )}
              </Box>
            ) : null}

            {disclaimer && (
              <Text
                as="div"
                color={disclaimerColor || 'white'}
                fontSize="sm"
                opacity={0.6}
                marginTop={[4, null, 6]}
                dangerouslySetInnerHTML={{
                  __html: sanitize(disclaimer),
                }}
              />
            )}
          </Box>
        </Box>

        {/* Image container - mobile and desktop */}
        <Box
          width={['100%', null, 'unset']}
          flex={[null, null, 2]}
          position="relative"
          zIndex="2"
        >
          {mobileImage ? (
            <Box display={['block', null, 'none']}>
              <Image
                as={GatsbyImage}
                image={mobileImage?.gatsbyImageData}
                alt={mobileImage?.description || ''}
                width="100%"
                imgStyle={{ objectPosition: 'top' }}
              />
            </Box>
          ) : null}

          {desktopImage ? (
            <Box
              display={['none', null, 'flex']}
              height={[null, null, DESKTOP_MAX_HEIGHT]}
            >
              <Image
                as={GatsbyImage}
                image={desktopImage?.gatsbyImageData}
                alt={desktopImage?.description || ''}
                width="100%"
                imgStyle={{ objectFit: 'contain' }}
              />
            </Box>
          ) : null}
        </Box>
      </Box>
    </SectionContainer>
  );
}
